/* eslint-disable react-refresh/only-export-components */
import { useToast } from '@chakra-ui/react';
import { CheckCircle } from '@kula-ai/icons/outline/CheckCircle';
import { InfoCircle } from '@kula-ai/icons/outline/InfoCircle';
import { XCircle } from '@kula-ai/icons/outline/XCircle';
import { STATUS_COLORS } from '../ui-library/components/constants';
import { Icon, Box } from '@chakra-ui/react';
import { KToast } from '@/ui-library/components/organisms/KToast/KToast';

export type IToastFunction = (_props: {
  title: string;
  description?: string;
  position?:
    | 'top'
    | 'top-right'
    | 'top-left'
    | 'bottom'
    | 'bottom-right'
    | 'bottom-left';
  status?: 'success' | 'error' | 'info' | 'message';
  duration?: number;
  isClosable?: boolean;
  /** Pass a stable id to prevent repeat fires from stacking. Chakra's
   *  useToast dedupes on id — a second call with the same id while the
   *  original is still visible is a no-op. */
  id?: string;
}) => void;

const ICONS = {
  success: CheckCircle,
  error: XCircle,
  info: InfoCircle,
  message: CheckCircle,
};

export const ToastIcon = ({
  status,
  iconStyles = {},
  iconWrapperProps = {},
}) => {
  const { icon } = STATUS_COLORS[status];
  const IconComponent = ICONS[status] || InfoCircle;

  return (
    <Box display="inline-block">
      <Box
        width="40px"
        height="40px"
        borderRadius="50%"
        border="2px solid"
        borderColor={`rgba(${icon}, 0.15)`}
        display="flex"
        padding="4px"
        alignItems="center"
        justifyContent="center"
        boxSizing="border-box"
        {...iconWrapperProps}
      >
        <Box
          width="100%"
          height="100%"
          borderRadius="50%"
          border="2px solid"
          display="flex"
          borderColor={`rgba(${icon}, 0.3)`}
          alignItems="center"
          justifyContent="center"
          boxSizing="border-box"
        >
          <Icon
            w="20px"
            h="20px"
            as={IconComponent}
            color={`rgba(${icon})`}
            {...iconStyles}
          />
        </Box>
      </Box>
    </Box>
  );
};

export const useShowToast = () => {
  const toast = useToast();
  const showToast = (props) => {
    const {
      title,
      description,
      position = 'bottom-left',
      status = 'success',
      duration = 6000,
      isClosable = true,
      id,
    } = props || {};

    // `toast.isActive(id)` short-circuits chakra's own dedupe, so
    // repeat clicks that fire the same `id` while the previous toast
    // is still on screen don't stack up.
    if (id && toast.isActive(id)) return;

    toast({
      ...(id ? { id } : {}),
      position,
      title,
      description,
      status,
      isClosable,
      duration,
      render: ({ id: renderId }) => (
        <KToast
          props={props}
          status={status}
          onClose={() => toast?.close(renderId)}
        />
      ),
    });
  };

  return showToast;
};
